import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const SearchBar = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ werkorders: [], klanten: [], projecten: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selIdx, setSelIdx] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Ctrl+K / Cmd+K: overal in de app naar de zoekbalk
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Klik buiten = sluiten
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced zoeken
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults({ werkorders: [], klanten: [], projecten: [] });
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await api.get(`/zoek?q=${encodeURIComponent(q.trim())}`);
        setResults({
          werkorders: res.data.werkorders || [],
          klanten: res.data.klanten || [],
          projecten: res.data.projecten || []
        });
        setSelIdx(0);
        setOpen(true);
      } catch (err) {
        console.error('Zoekfout:', err);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const navigateTo = (path) => {
    setOpen(false);
    setQ('');
    if (inputRef.current) inputRef.current.blur();
    navigate(path);
  };

  // Platte lijst in dezelfde volgorde als gerenderd — voor pijltjes + Enter
  const flat = [
    ...results.werkorders.map(w => `/werkorder/${w.id}`),
    ...results.klanten.map(k => `/klant/${k.id}`),
    ...results.projecten.map(p => `/projecten/${p.id}`),
  ];
  const totaal = flat.length;

  // Geselecteerde rij in beeld houden
  useEffect(() => {
    const el = wrapperRef.current && wrapperRef.current.querySelector('[data-selrow="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [selIdx]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      if (inputRef.current) inputRef.current.blur();
      return;
    }
    if (!open || totaal === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelIdx(i => (i + 1) % totaal); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelIdx(i => (i - 1 + totaal) % totaal); }
    else if (e.key === 'Enter') { e.preventDefault(); navigateTo(flat[Math.min(selIdx, totaal - 1)]); }
  };

  const rijStijl = (actief) => ({
    padding: '10px 12px', cursor: 'pointer', borderTop: '1px solid #f1f5f9',
    background: actief ? '#eef2ff' : 'white',
  });

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: 1, maxWidth: 480, margin: '0 16px' }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, pointerEvents: 'none' }}>{'\u{1F50D}'}</span>
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Zoek werkorders, klanten, projecten..."
          style={{
            width: '100%',
            padding: '10px 12px 10px 36px',
            border: '1px solid #cbd5e1',
            borderRadius: 8,
            fontSize: 14,
            outline: 'none',
            background: 'white',
            boxSizing: 'border-box'
          }}
        />
        {!q && (
          <span style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 11, color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 5,
            padding: '2px 6px', background: '#f8fafc', pointerEvents: 'none', fontWeight: 600
          }}>
            Ctrl K
          </span>
        )}
        {q && (
          <button
            onClick={() => { setQ(''); setOpen(false); }}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 18, color: '#94a3b8', padding: 4
            }}
            title="Wissen"
          >
            {'\u00d7'}
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'white', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          border: '1px solid #e2e8f0', maxHeight: 480, overflowY: 'auto', zIndex: 1000
        }}>
          {loading && (
            <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
              Zoeken...
            </div>
          )}
          {!loading && totaal === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
              Geen resultaten gevonden
            </div>
          )}

          {results.werkorders.length > 0 && (
            <div>
              <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {'\u{1F4CB}'} Werkorders ({results.werkorders.length})
              </div>
              {results.werkorders.map((w, i) => {
                const actief = selIdx === i;
                return (
                  <div
                    key={'w-' + w.id}
                    data-selrow={actief ? 'true' : undefined}
                    onClick={() => navigateTo(`/werkorder/${w.id}`)}
                    onMouseEnter={() => setSelIdx(i)}
                    style={rijStijl(actief)}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
                      {w.ordernummer || 'WO-' + w.id} {'\u2014'} {w.titel}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {w.klant_naam || 'Geen klant'} {'\u00b7'} {w.status}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {results.klanten.length > 0 && (
            <div>
              <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {'\u{1F464}'} Klanten ({results.klanten.length})
              </div>
              {results.klanten.map((k, i) => {
                const idx = results.werkorders.length + i;
                const actief = selIdx === idx;
                return (
                  <div
                    key={'k-' + k.id}
                    data-selrow={actief ? 'true' : undefined}
                    onClick={() => navigateTo(`/klant/${k.id}`)}
                    onMouseEnter={() => setSelIdx(idx)}
                    style={rijStijl(actief)}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{k.naam}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {[k.adres, k.plaats].filter(Boolean).join(', ') || 'Geen adres'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {results.projecten.length > 0 && (
            <div>
              <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {'\u{1F4C1}'} Projecten ({results.projecten.length})
              </div>
              {results.projecten.map((p, i) => {
                const idx = results.werkorders.length + results.klanten.length + i;
                const actief = selIdx === idx;
                return (
                  <div
                    key={'p-' + p.id}
                    data-selrow={actief ? 'true' : undefined}
                    onClick={() => navigateTo(`/projecten/${p.id}`)}
                    onMouseEnter={() => setSelIdx(idx)}
                    style={rijStijl(actief)}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{p.naam}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {p.project_code || 'Geen code'} {'\u00b7'} {p.status || ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
